import { useCallback } from 'react';
import { AxiosError } from 'axios';
import { useToast } from '@heidi-pay/heidi-common-fe/hooks/useToast/useToast';
import { IError } from '@heidi-pay/heidi-common-fe/types';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useI18n } from '@hooks/useI18n/useI18n';
import { useModal } from '@hooks/useModal';
import { ModalStatusRouter } from '@hooks/useModal/components';
import { getModalDetails } from '@hooks/useModal/getModalDetails';
import { Modal } from '@hooks/useModal/types';
import { usePortalError } from '@hooks/usePortalError/usePortalError';
import { QueryKeys } from '@utils/constants';
import { Endpoints } from '@utils/enums';
import { postDataToEndpoint } from '@utils/postDataToEndpoint';
import { IPayload, IWriteOffModalProps } from './types';
import { WriteOffForm } from './WriteOffForm';

export const useWriteOffModal = (props: IWriteOffModalProps): Modal => {
  const { contractUUID, externalContractUUID, remainder, totalInterest } =
    props;
  const { translate } = useI18n();
  const { message } = useToast(translate);
  const { handleAxiosError } = usePortalError();

  const modal = useModal({
    sharedProps: props,
    modalName: 'writeOffModal',
    buttonTranslationKey: 'OPS_PORTAL.TITLE.CREATE_WRITE_OFF',
    permissions: ['ops_portal.can_trigger_write_offs'],
  });
  const { closeModal, formStatusMessage } = modal;

  const queryClient = useQueryClient();

  const { isLoading, mutate } = useMutation<null, AxiosError<IError>, IPayload>(
    data => {
      const endpoint = `${Endpoints.CreateWriteOff}/${externalContractUUID}`;
      return postDataToEndpoint({ endpoint, requestData: data });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          QueryKeys.CONTRACT_DETAILS_DATA,
          externalContractUUID,
        ]);
        queryClient.invalidateQueries([
          QueryKeys.MONETA_BALANCE_DATA,
          contractUUID,
        ]);
        queryClient.invalidateQueries([
          QueryKeys.MONETA_CONTRACT_DATA,
          externalContractUUID,
        ]);

        message.success('OPS_PORTAL.STATUS.WRITE_OFF_SUCCESS');
        closeModal();
      },
      onError: error => {
        handleAxiosError(error, 'OPS_PORTAL.STATUS.WRITE_OFF_FAILED');
        closeModal();
      },
    },
  );

  const handleSubmit = useCallback(
    (data: IPayload) => {
      if (externalContractUUID) {
        mutate({ amount: data.amount });
      }
    },
    [externalContractUUID, mutate],
  );

  const formID = 'createWriteOff';

  const modalContent = (
    <ModalStatusRouter
      formStatusMessage={formStatusMessage}
      isLoading={isLoading}
    >
      <>
        <p>{translate('OPS_PORTAL.PAGE.WRITE_OFF_MODAL_TEXT')}</p>
        <WriteOffForm
          formID={formID}
          handleSubmit={handleSubmit}
          remainder={remainder}
          totalInterest={totalInterest}
        />
      </>
    </ModalStatusRouter>
  );

  const modalProps = {
    formID,
    isFormProcessing: isLoading,
    isLoading,
    modalContent,
    title: translate('OPS_PORTAL.TITLE.CREATE_WRITE_OFF'),
  };

  return getModalDetails({ modal, modalProps });
};
